import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useIsSelfServeBilling } from "@/hooks/useIsSelfServeBilling";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { isRealNativePlatform } from "@/native/platform";
import { trackBillingPlanChangeClicked } from "@/utils/analytics";
import { formatInteger } from "@/utils/currency";
import { formatTrialDaysRemaining } from "@/utils/trial";
import { useNavigate } from "react-router-dom";

/**
 * Trial days and credits, stated in the rail footer so neither number needs a
 * trip through settings to read.
 */
export function TrialStatusCard() {
  const { isTrialing, daysRemaining, includedCredits, creditsRemaining } =
    useTrialStatus();
  const { data: userInfo } = useUserInfo();
  const isSelfServeBilling = useIsSelfServeBilling();
  const openSettings = useOpenSettings();
  const navigate = useNavigate();

  // Billing/usage UI is hidden only in the real iOS container (Apple 3.1.1).
  if (isRealNativePlatform()) return null;

  if (!isTrialing) return null;

  const daysLabel = formatTrialDaysRemaining(daysRemaining);
  const isExhausted = includedCredits > 0 && creditsRemaining === 0;
  const tone = isExhausted ? "var(--lc-blocked)" : "var(--lc-warning)";
  const creditsLabel =
    includedCredits > 0
      ? creditsRemaining === 0
        ? "No credits left"
        : creditsRemaining === 1
          ? "1 credit left"
          : `${formatInteger(creditsRemaining)} credits left`
      : null;
  const action = isSelfServeBilling
    ? { label: "View plans", run: () => navigate(ROUTES.BILLING.PLANS) }
    : { label: "View usage", run: () => openSettings(ROUTES.SETTINGS.USAGE) };

  return (
    <div className="px-2 pt-2">
      <button
        type="button"
        aria-label={`Free trial — ${[daysLabel, creditsLabel]
          .filter(Boolean)
          .join(", ")}. ${action.label}.`}
        onClick={() => {
          trackBillingPlanChangeClicked({
            currentPlan: userInfo?.subscription?.plan_name,
            source: "trial_rail",
          });
          action.run();
        }}
        className="group flex w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left outline-none transition-colors hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
      >
        <span className="flex w-full items-center gap-1.5">
          <span
            className="size-1.5 shrink-0 rounded-full"
            style={{ background: tone }}
          />
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-n-80">
            {daysLabel ? `Free trial · ${daysLabel}` : "Free trial"}
          </span>
          <span className="shrink-0 text-2xs font-medium text-rust group-hover:underline">
            {action.label}
          </span>
        </span>
        {creditsLabel && (
          <span
            className={`pl-3 text-2xs ${
              creditsRemaining === 0 ? "text-lc-blocked" : "text-n-50"
            }`}
          >
            {creditsLabel}
          </span>
        )}
      </button>
    </div>
  );
}
